import React from "react";
import { InputNumber } from "antd";
import classNames from "classnames";
import {
  AGREED_DELTA_FIELD,
  AGREED_DELTA_LABELS,
  AGREED_DELTA_UNIT_LABEL,
  agreedDeltaGroupTitle,
  agreedDeltaSideSuggestions,
  displayedAgreedDeltaUnit,
  isValidAgreedDeltaNumber,
  otherAgreedDeltaUnit,
  parseAgreedDeltaNumber,
  visibleAgreedDeltaFields,
} from "helpers/TeamRun/agreedDelta";
import "./AgreedDeltaInputs.scss";

function AgreedDeltaUnitSuffix({ unit, title, disabled, onToggle }) {
  const nextUnit = otherAgreedDeltaUnit(unit);

  return (
    <button
      type="button"
      className="agreed-delta-inputs__unit"
      disabled={disabled}
      title={`Switch to ${nextUnit}`}
      aria-label={`${title} ${AGREED_DELTA_UNIT_LABEL[unit]}. Switch to ${AGREED_DELTA_UNIT_LABEL[nextUnit]}`}
      onClick={onToggle}
    >
      {unit}
    </button>
  );
}

function fieldIsInvalid(field, value) {
  if (field.key !== AGREED_DELTA_FIELD.DELTA_T) return false;
  if (value === null || value === undefined || value === "") return false;
  return !isValidAgreedDeltaNumber(value);
}

const AgreedDeltaInputs = ({
  groups = [],
  onChange,
  onToggleUnit,
  onApplySuggestion,
  darkTheme = false,
  disabled = false,
}) => {
  if (!groups.length) return null;

  const fields = visibleAgreedDeltaFields();

  return (
    <section
      className={classNames("agreed-delta-inputs", {
        "agreed-delta-inputs--dark": darkTheme,
      })}
      aria-label={AGREED_DELTA_LABELS.SECTION}
    >
      <h6 className="agreed-delta-inputs__heading">
        {AGREED_DELTA_LABELS.SECTION}
      </h6>
      <div className="agreed-delta-inputs__list">
        {groups.map((group, index) => {
          const title = agreedDeltaGroupTitle(group);

          return (
            <div
              key={`${group.underlying}-${group.underlying_tenor}-${index}`}
              className="agreed-delta-inputs__row"
            >
              <h6 className="agreed-delta-inputs__label" title={title}>
                {title}
              </h6>
              <div className="agreed-delta-inputs__fields">
                {fields.map((field) => {
                  const inputId = `agreed-delta-${index}-${field.key}`;
                  const numericValue = parseAgreedDeltaNumber(group[field.key]);
                  const invalid = fieldIsInvalid(field, group[field.key]);
                  const unit = displayedAgreedDeltaUnit(group.delta_unit);
                  const showUnit = field.key === AGREED_DELTA_FIELD.DELTA_T;
                  const sideSuggestions = showUnit
                    ? agreedDeltaSideSuggestions(group)
                    : [];

                  return (
                    <div key={field.key} className="agreed-delta-inputs__field">
                      <label
                        className="agreed-delta-inputs__field-label"
                        htmlFor={inputId}
                      >
                        {field.label}
                      </label>
                      <InputNumber
                        id={inputId}
                        value={numericValue}
                        placeholder={field.placeholder}
                        disabled={disabled}
                        min={0}
                        max={100}
                        step={1}
                        status={invalid ? "error" : undefined}
                        addonAfter={
                          showUnit ? (
                            <AgreedDeltaUnitSuffix
                              unit={unit}
                              title={title}
                              disabled={disabled}
                              onToggle={() => onToggleUnit(index)}
                            />
                          ) : undefined
                        }
                        aria-label={`${title} ${field.label}`}
                        aria-invalid={invalid}
                        onChange={(value) => onChange(index, field.key, value)}
                      />
                      {sideSuggestions.length > 0 && !disabled ? (
                        <div className="agreed-delta-inputs__suggestions">
                          {sideSuggestions.map((line) => (
                            <button
                              key={line.side}
                              type="button"
                              className="agreed-delta-inputs__suggestion"
                              aria-label={`${title} use ${line.side} ${AGREED_DELTA_UNIT_LABEL[line.unit]} ${line.text}`}
                              onClick={() => onApplySuggestion?.(index, line)}
                            >
                              {line.side} {line.text} {line.unit}
                            </button>
                          ))}
                        </div>
                      ) : null}
                      {invalid && field.hint ? (
                        <p className="agreed-delta-inputs__hint">{field.hint}</p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default AgreedDeltaInputs;
